import React from "react";
import { useSelector, shallowEqual } from "react-redux";
import { TooltipKeys } from '../../tooltips/TooltipKeys';
import UIHeaders from '../subcomponents/UIHeaders';
import UIInsets from '../subcomponents/UIInsets';
import UINextStep from '../subcomponents/UINextStep';
import UIOptions from '../subcomponents/UIOptions';
import UIPreviousStep from '../subcomponents/UIPreviousStep';
import UIProjectOptions from '../subcomponents/UIProjectOptions';
import UITextDescription from '../subcomponents/UITextDescription';
import UIVoice from '../subcomponents/UIVoice';
import UIInsetsProps from '../subcomponents/UIInsetsProps';
import subComponentTypes from '../subcomponents/types';
import UIEditorComponentRenderer from '../UIEditorComponentRenderer';

export default function TCustom(props) 
{
	const customMap = useSelector(
		(state) =>
			props.slide &&
          state.uiModule.slideMaster[props.slide] &&
          state.uiModule.slideMaster[props.slide].customMap
				? state.uiModule.slideMaster[props.slide].customMap
				: props.step &&
              state.uiModule.ui[props.step] &&
              state.uiModule.ui[props.step].customMap
					? state.uiModule.ui[props.step].customMap
					: null,
		shallowEqual
	);

	function getRawProperties()
	{
		let properties = {}
		if(!customMap) return properties;
		for(let i = 0 ; i< customMap.length; i++)
		{
			let key = customMap[i].type;
			switch(key)
			{
				case subComponentTypes.HEADERS:
					properties[subComponentTypes.HEADERS] = { ...UIHeaders({ ...props, size:customMap[i].size, anchor:'bottom', align:'left' }) };
					break;
				case subComponentTypes.INSETS:
					properties[subComponentTypes.INSETS] = { ...UIInsetsProps({ ...props, size:customMap[i].size }) };
					break;
				case subComponentTypes.NEXT_STEP:
					properties[subComponentTypes.NEXT_STEP] = { ...UINextStep({ ...props }) };
					break;
				case subComponentTypes.OPTIONS:
					properties[subComponentTypes.OPTIONS] = { ...UIOptions({ ...props, size:customMap[i].size, top:20 }) };
					break;
				case subComponentTypes.PREVIOUS_STEP:
					properties[subComponentTypes.PREVIOUS_STEP] = { ...UIPreviousStep({ ...props }) };
					break;
				case subComponentTypes.PROJECT_OPTIONS:
					properties[subComponentTypes.PROJECT_OPTIONS] = { ...UIProjectOptions({ ...props }) };
					break;
				case subComponentTypes.TEXT_DESCRIPTION:
					properties[subComponentTypes.TEXT_DESCRIPTION] = { ...UITextDescription({ ...props }) };
					break;
				case subComponentTypes.VOICE:
					properties[subComponentTypes.VOICE] = { ...UIVoice({ ...props }) };
					break;
			}
		}
		return properties;
	}

	if(props.getProperties)
	{
		return getRawProperties();
	}

	function generateInsetsData(insetsProperties)
	{
		// console.log(insetsProperties);
		const subcomponent = 'Insets';

		let generatedInsets = [];
		let insetCounter = 1;
		while(insetsProperties[`inset ${insetCounter} title`])
		{
			generatedInsets.push({
				type: 'group',
				key: `inset ${insetCounter} group`,
				collapsed: true,
				title: `Inset ${insetCounter}`,
				titleAddon: {
					key: `inset ${insetCounter} title`,
					subcomponent: subcomponent
				},
				subcomponent: null,
				decorator: {
					...insetsProperties[`inset ${insetCounter} type`],
					key: `inset ${insetCounter} type`,
					type: 'toggle-icon',
					subcomponent: subcomponent,
					falseValue: 'invisible',
					trueValue: 'image',
					title: '',
				},
				children: [
					{
						...insetsProperties[`inset ${insetCounter} type`],
						key: `inset ${insetCounter} type`,
						subcomponent: subcomponent,
						title: "Type",
					},
					{
						...insetsProperties[`inset ${insetCounter} height`],
						key: `inset ${insetCounter} height`,
						subcomponent: subcomponent,
						title: "Height",
					},
					{
						...insetsProperties[`inset ${insetCounter} anchor`],
						key: `inset ${insetCounter} anchor`,
						subcomponent: subcomponent,
						title: "Anchor",
					}, //here the shitshow begins
					{
						...insetsProperties[`inset ${insetCounter} image`],
						key: `inset ${insetCounter} image`,
						subcomponent: subcomponent,
						title: "Image",
						dependingOnProperty: { key: `inset ${insetCounter} type`, subcomponent: subcomponent },
						dependingOnValues: ['image', 'step', 'video', 'audio', 'url'],
					},
					{
						...insetsProperties[`inset ${insetCounter} audio`],
						key: `inset ${insetCounter} audio`,
						subcomponent: subcomponent,
						title: "Audio",
						dependingOnProperty: { key: `inset ${insetCounter} type`, subcomponent: subcomponent },
						dependingOnValues: ['audio'],
					},
					{
						...insetsProperties[`inset ${insetCounter} camera`],
						key: `inset ${insetCounter} camera`,
						subcomponent: subcomponent,
						title: "Camera",
						dependingOnProperty: { key: `inset ${insetCounter} type`, subcomponent: subcomponent },
						dependingOnValues: ['camera'],
					},
					{
						...insetsProperties[`inset ${insetCounter} title`],
						key: `inset ${insetCounter} title`,
						subcomponent: subcomponent,
						title: "Title",
						dependingOnProperty: { key: `inset ${insetCounter} type`, subcomponent: subcomponent },
						dependingOnValues: ['camera'],
					},
					{
						...insetsProperties[`inset ${insetCounter} step`],
						key: `inset ${insetCounter} step`,
						subcomponent: subcomponent,
						title: "Target",
						dependingOnProperty: { key: `inset ${insetCounter} type`, subcomponent: subcomponent },
						dependingOnValues: ['step'],
					},
					{
						...insetsProperties[`inset ${insetCounter} url`],
						key: `inset ${insetCounter} url`,
						subcomponent: subcomponent,
						title: "URL",
						dependingOnProperty: { key: `inset ${insetCounter} type`, subcomponent: subcomponent },
						dependingOnValues: ['url'],
					},
					{
						...insetsProperties[`inset ${insetCounter} video`],
						key: `inset ${insetCounter} video`,
						subcomponent: subcomponent,
						title: "Video",
						dependingOnProperty: { key: `inset ${insetCounter} type`, subcomponent: subcomponent },
						dependingOnValues: ['video'],
					},
				]
			})
			insetCounter++;
		}
 
		return {
			type: 'group',
			key: 'insets group',
			title: 'Insets',
			subcomponent: null,
			children: generatedInsets
		};
	}

	function generateHeadersData(properties)
	{
		const subcomponent = 'Headers';
		return  {
			type: 'group',
			key: 'headers group',
			title: 'Headers',
			subcomponent: null,
			decorator: {
				...properties['visible'],
				key: 'visible',
				type: 'toggle-icon',
				subcomponent: subcomponent,
				title: '',
			},
			children: [
				{
					...properties['anchor'],
					key: 'anchor',
					subcomponent: subcomponent,
					title: 'Anchor',
				},
				{
					...properties['alignment'],
					key: 'alignment',
					subcomponent: subcomponent,
					title: 'Alignment',
				},
				{
					...properties['header spacing'],
					key: 'header spacing',
					subcomponent: subcomponent,
					title: 'Spacing',
				},
			].concat((()=>
			{
				let generatedHeaders = [];
				let headerCounter = 1;
				while(properties[`header ${headerCounter} text`])
				{
					generatedHeaders.push({
						type: 'group',
						key: `header ${headerCounter} group`,
						collapsed: true,
						title: `Header ${headerCounter}`,
						titleAddon: {
							key: `header ${headerCounter} text`,
							subcomponent: subcomponent
						},
						subcomponent: null,
						decorator: {
							...properties[`header ${headerCounter} visible`],
							key: `header ${headerCounter} visible`,
							type: 'toggle-icon',
							subcomponent: subcomponent,
							title: '',
						},
						children: [
							{
								...properties[`header ${headerCounter} text`],
								key: `header ${headerCounter} text`,
								subcomponent: subcomponent,
								title: "",
							},
							{
								type: 'color-alpha',
								title: "Text",
								color: {
									...properties[`header ${headerCounter} text color`],
									key: `header ${headerCounter} text color`,
									subcomponent: subcomponent,
								},
								alpha: {
									...properties[`header ${headerCounter} text alpha`],
									key: `header ${headerCounter} text alpha`,
									subcomponent: subcomponent,
								},
							},
							{
								type: 'color-alpha',
								title: "Background",
								color: {
									...properties[`header ${headerCounter} background color`],
									key: `header ${headerCounter} background color`,
									subcomponent: subcomponent,
								},
								alpha: {
									...properties[`header ${headerCounter} background alpha`],
									key: `header ${headerCounter} background alpha`,
									subcomponent: subcomponent,
								},
							},
							{
								type: 'color-alpha',
								title: "Stroke",
								color: {
									...properties[`header ${headerCounter} shadow color`],
									key: `header ${headerCounter} shadow color`,
									subcomponent: subcomponent,
								},
								alpha: {
									...properties[`header ${headerCounter} shadow alpha`],
									key: `header ${headerCounter} shadow alpha`,
									subcomponent: subcomponent,
								},
							},
							{
								...properties[`header ${headerCounter} link`],
								key: `header ${headerCounter} link`,
								subcomponent: subcomponent,
								title: "Target",
							},
						]
					})
					headerCounter++;
				}
				return generatedHeaders;
			})())
		}
	}

	function generateOptionsData(properties)
	{
		const subcomponent = 'Options';

		return {
			type: 'group',
			key: 'options group',
			title: 'Options',
			subcomponent: null,
			decorator: {
				...properties['visible'],
				key: 'visible',
				type: 'toggle-icon',
				subcomponent: subcomponent,
				title: '',
			},
			children: [
				{
					...properties['layout'],
					key: 'layout',
					subcomponent: subcomponent,
					title: 'Layout',
				},
				{
					...properties['option spacing'],
					key: 'option spacing',
					subcomponent: subcomponent,
					title: 'Spacing',
				},
				{
					...properties['option top padding'],
					key: 'option top padding',
					subcomponent: subcomponent,
					title: 'Top padding',
				},
			].concat((()=>
			{
				let generatedOptions = [];
				let optionCounter = 1;
				while(properties[`option ${optionCounter} text`])
				{
					generatedOptions.push({
						type: 'group',
						key: `option ${optionCounter} group`,
						collapsed: true,
						title: `Option ${optionCounter}`,
						titleAddon: {
							key: `option ${optionCounter} text`,
							subcomponent: subcomponent
						},
						subcomponent: null,
						decorator: {
							...properties[`option ${optionCounter} visible`],
							key: `option ${optionCounter} visible`,
							type: 'toggle-icon',
							subcomponent: subcomponent,
							title: '',
						},
						children: [
							{
								...properties[`option ${optionCounter} text`],
								key: `option ${optionCounter} text`,
								subcomponent: subcomponent,
								title: "",
							},
							{
								type: 'color-alpha',
								title: "Text",
								color: {
									...properties[`option ${optionCounter} text color`],
									key: `option ${optionCounter} text color`,
									subcomponent: subcomponent,
								},
								alpha: {
									...properties[`option ${optionCounter} text alpha`],
									key: `option ${optionCounter} text alpha`,
									subcomponent: subcomponent,
								},
							},
							{
								type: 'color-alpha',
								title: "Background",
								color: {
									...properties[`option ${optionCounter} background color`],
									key: `option ${optionCounter} background color`,
									subcomponent: subcomponent,
								},
								alpha: {
									...properties[`option ${optionCounter} background alpha`],
									key: `option ${optionCounter} background alpha`,
									subcomponent: subcomponent,
								},
							},
							{
								type: 'color-alpha',
								title: "Stroke",
								color: {
									...properties[`option ${optionCounter} shadow color`],
									key: `option ${optionCounter} shadow color`,
									subcomponent: subcomponent,
								},
								alpha: {
									...properties[`option ${optionCounter} shadow alpha`],
									key: `option ${optionCounter} shadow alpha`,
									subcomponent: subcomponent,
								},
							},
							{
								...properties[`option ${optionCounter} link`],
								key: `option ${optionCounter} link`,
								subcomponent: subcomponent,
								title: "Target",
							},
						]
					})
					optionCounter++;
				}
				return generatedOptions;
			})())
		}
	}

	function generateProjectOptionsData(properties)
	{
		const subcomponent = 'ProjectOptions';

		return {
			type: 'group',
			key: 'project options group',
			title: 'Project Options',
			subcomponent: null,
			decorator: {
				...properties['visible'],
				key: 'visible',
				type: 'toggle-icon',
				subcomponent: subcomponent,
				title: '',
			},
			children: [
				{
					...properties['scenario logo'],
					key: 'scenario logo',
					subcomponent: subcomponent,
					title: "Scenario Logo",
				},
				{
					...properties['company logo'],
					key: 'company logo',
					subcomponent: subcomponent,
					title: "Company Logo",
				},
				{
					...properties.title,
					key: 'title',
					subcomponent: subcomponent,
					title: "",
				},
				{
					type: 'color-alpha',
					title: "Title",
					color: {
						...properties['title color'],
						key: 'title color',
						subcomponent: subcomponent,
					},
					alpha: {
						...properties['title alpha'],
						key: 'title alpha',
						subcomponent: subcomponent,
					},
				},
				{
					...properties.subtitle,
					key: 'subtitle',
					subcomponent: subcomponent,
					title: "",
				},
				{
					type: 'color-alpha',
					title: "Subtitle",
					color: {
						...properties['subtitle color'],
						key: 'subtitle color',
						subcomponent: subcomponent,
					},
					alpha: {
						...properties['subtitle alpha'],
						key: 'subtitle alpha',
						subcomponent: subcomponent,
					},
				},
				{
					type: 'color-alpha',
					title: "Background",
					color: {
						...properties['background color'],
						key: 'background color',
						subcomponent: subcomponent,
					},
					alpha: {
						...properties['background alpha'],
						key: 'background alpha',
						subcomponent: subcomponent,
					},
				},
				{
					...properties['anchor'],
					key: 'anchor',
					subcomponent: subcomponent,
					title: "Anchor",
				},
			]
		}
	}

	function generateUiDescriptionData(properties)
	{
		const subcomponent = 'TextDescription';

		return {
			type: 'group',
			key: 'text description group',
			title: 'Text Description',
			subcomponent: null,
			decorator: {
				...properties['visible'],
				key: 'visible',
				type: 'toggle-icon',
				subcomponent: subcomponent,
				title: '',
			},
			children: [
				{
					...properties.description,
					key: 'description',
					subcomponent: subcomponent,
					title: "",
				},
				{
					type: 'color-alpha',
					title: "Text",
					color: {
						...properties['text color'],
						key: 'text color',
						subcomponent: subcomponent,
					},
					alpha: {
						...properties['text alpha'],
						key: 'text alpha',
						subcomponent: subcomponent,
					},
				},
				{
					type: 'color-alpha',
					title: "Background",
					color: {
						...properties['background color'],
						key: 'background color',
						subcomponent: subcomponent,
					},
					alpha: {
						...properties['background alpha'],
						key: 'background alpha',
						subcomponent: subcomponent,
					},
				},
				{
					type: 'color-alpha',
					title: "Stroke",
					color: {
						...properties['shadow color'],
						key: 'shadow color',
						subcomponent: subcomponent,
					},
					alpha: {
						...properties['shadow alpha'],
						key: 'shadow alpha',
						subcomponent: subcomponent,
					},
				},
				{
					...properties['text alignment'],
					key: 'text alignment',
					type: 'align-text',
					subcomponent: subcomponent,
					title: "Alignment",
				},
				{
					...properties['font size'],
					key: 'font size',
					subcomponent: subcomponent,
					title: "Text Size",
				},
				{
					...properties['preset'],
					key: 'preset',
					subcomponent: subcomponent,
					title: "Preset",
				},
			]
		}
	}

	function generateNextStepData(properties)
	{
		const subcomponent = 'NextStep';

		return {
			...properties.visible,
			key: 'visible',
			subcomponent: subcomponent,
			title: 'Next Step',
		}
	}

	function generatePreviousStepData(properties)
	{
		const subcomponent = 'PreviousStep';

		return {
			...properties.visible,
			key: 'visible',
			subcomponent: subcomponent,
			title: 'Previous Step',
		}
	}

	function generateVoiceData(properties)
	{
		const subcomponent = 'Voice';

		return {
			type: 'group',
			key: 'voice group',
			tooltip: TooltipKeys.UIVoice,
			title: 'Voice',
			subcomponent: null,
			children: [
				{
					...properties.Voice,
					key: 'Voice',
					subcomponent: subcomponent,
					title: "",
				}
			]
		}
	}

	function repackProperties()
	{
		let rawProperties = getRawProperties();
		// console.log(rawProperties);
		let flatProperties = [];
		for(let [subcomponentKey, subcomponentValue] of Object.entries(rawProperties))
		{
			switch(subcomponentKey)
			{
				case subComponentTypes.INSETS:
					flatProperties.push(generateInsetsData(subcomponentValue));
					break;
				case subComponentTypes.HEADERS:
					flatProperties.push(generateHeadersData(subcomponentValue.props.properties));
					break;
				case subComponentTypes.OPTIONS:
					flatProperties.push(generateOptionsData(subcomponentValue.props.properties));
					break;
				case subComponentTypes.PROJECT_OPTIONS:
					flatProperties.push(generateProjectOptionsData(subcomponentValue.props.properties));
					break;
				case subComponentTypes.TEXT_DESCRIPTION:
					flatProperties.push(generateUiDescriptionData(subcomponentValue.props.properties));
					break;
				case subComponentTypes.NEXT_STEP:
					flatProperties.push(generateNextStepData(subcomponentValue.props.properties));
					break;
				case subComponentTypes.PREVIOUS_STEP:
					flatProperties.push(generatePreviousStepData(subcomponentValue.props.properties));
					break;
				case subComponentTypes.VOICE:
					flatProperties.push(generateVoiceData(subcomponentValue.props.properties));
					break;
			}
		}

		return <UIEditorComponentRenderer
			{...props}
			properties={flatProperties}
		/>;
	}

	const renderActiveSubcomponents = (() => 
	{
		let subComponents = [];
		if(!customMap) return subComponents;
		for(let i = 0 ; i < customMap.length; i++)
		{
			let key = customMap[i].type;
			switch(key)
			{
				case subComponentTypes.HEADERS:
					subComponents.push(<UIHeaders key={i} size={customMap[i].size} anchor={'bottom'} align={'left'} {...props} />);
					break;
				case subComponentTypes.INSETS:
					subComponents.push(<UIInsets key={i} size={customMap[i].size} {...props} />);
					break;
				case subComponentTypes.NEXT_STEP:
					subComponents.push(<UINextStep key={i} {...props} />);
					break;
				case subComponentTypes.OPTIONS:
					subComponents.push(<UIOptions key={i} {...props} size={customMap[i].size} top={20} />);
					break;
				case subComponentTypes.PREVIOUS_STEP:
					subComponents.push(<UIPreviousStep key={i} {...props} />);
					break;
				case subComponentTypes.PROJECT_OPTIONS:
					subComponents.push(<UIProjectOptions key={i} {...props} />);
					break;
				case subComponentTypes.TEXT_DESCRIPTION:
					subComponents.push(<UITextDescription key={i} {...props} />);
					break;
				case subComponentTypes.VOICE:
					subComponents.push(<UIVoice key={i} {...props} />);
					break;
			}
		}
		return subComponents;
	})();

	return props.context === 'editor' ? 
		<>
			{repackProperties()}
		</>
		:<>{renderActiveSubcomponents}</>;
}
